import { Box, styled, type BoxProps, type Theme } from "@mui/material";

type MarqueeTextProps = {
  text: string;
  duration?: number;
  repeat?: number;
} & BoxProps;

const responsiveStyles = (theme: Theme) => ({
  minHeight: "200px",
  fontSize: "200px",
  padding: theme.spacing(4, 8),
  [theme.breakpoints.down("desktop-l")]: {
    minHeight: "164px",
    fontSize: "164px",
    padding: theme.spacing(4, 3),
  },
  [theme.breakpoints.down("desktop-m")]: {
    minHeight: "132px",
    fontSize: "132px",
    padding: theme.spacing(3, 2),
  },
  [theme.breakpoints.down("desktop-s")]: {
    minHeight: "80px",
    fontSize: "80px",
    padding: theme.spacing(3, 2),
  },

  [theme.breakpoints.down("tablet")]: {
    minHeight: "60px",
    fontSize: "60px",
    padding: theme.spacing(2),
  },
});

const MarqueeContainer = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  whiteSpace: "nowrap",
  ...responsiveStyles(theme),
}));

const MarqueeTrack = styled(Box, {
  shouldForwardProp: (prop) => prop !== "duration",
})<{ duration: number }>(({ theme, duration }) => ({
  display: "flex",
  width: "max-content",
  willChange: "transform",
  animation: `marquee ${duration}s linear infinite`,
  gap: "200px",

  [theme.breakpoints.down("desktop-s")]: {
    gap: "150px",
  },

  [theme.breakpoints.down("tablet")]: {
    gap: "100px",
  },

  "@keyframes marquee": {
    from: {
      transform: "translateX(0)",
    },
    to: {
      transform: "translateX(-50%)",
    },
  },
}));

const MarqueeItem = styled(Box)(({ theme }) => ({
  display: "inline-block",
  color: theme.palette.primary.container,
  textTransform: "uppercase",
  fontFamily: "Noto Sans",
  fontWeight: 800,
  lineHeight: 1,
}));

export default function MarqueeText({ text, duration = 18, repeat = 2, sx, ...props }: MarqueeTextProps) {
  return (
    <MarqueeContainer sx={sx} {...props}>
      <MarqueeTrack duration={duration}>
        {Array.from({ length: repeat }).map((_, index) => (
          <MarqueeItem key={index}>{text}</MarqueeItem>
        ))}
      </MarqueeTrack>
    </MarqueeContainer>
  );
}
